import { z } from 'zod';
import { productSizeOptionSchema } from './catalog';
import { paginatedSchema, paginationSchema, normalizedUuidSchema as uuidSchema } from './common';
import { designThumbnailViewSchema } from './design-api';
import { guestContactMethodSchema } from './guest-contact';
import {
  QUOTE_INT_MAX,
  QUOTE_INT_MIN,
  quoteIntegerSchema,
  quoteQuantitySchema,
  splitVatInclusiveTotal,
} from './quote-primitives';
import { refineDestinationSizes } from './quote-sizes';
import {
  destinationListSchema,
  memberDestinationInputSchema,
  shippingFeeSettingsSchema,
} from './shipping';

/** 원시 정의는 `./quote-primitives`(잎 모듈)에 있다 — index 의 `export *` 가 두 번 내보내지 않도록 여기서만 재노출한다. */
export {
  QUOTE_INT_MAX,
  QUOTE_INT_MIN,
  quoteIntegerSchema,
  quoteQuantitySchema,
  splitVatInclusiveTotal,
};

export const QUOTE_REQUEST_STATUSES = [
  'REQUESTED',
  'ISSUED',
  'REVISION_REQUESTED',
  'ORDERED',
  'DECLINED',
  'REJECTED',
  'CANCELED',
  'EXPIRED',
] as const;
export type QuoteRequestStatus = (typeof QUOTE_REQUEST_STATUSES)[number];
export const quoteRequestStatusSchema = z.enum(QUOTE_REQUEST_STATUSES);
export const DEFAULT_ADMIN_QUOTE_STATUSES: readonly QuoteRequestStatus[] = [
  'REQUESTED',
  'REVISION_REQUESTED',
];

/** 날짜 형식과 윤년을 검사한다. 한국 오늘과의 비교는 서버의 quote-policy가 담당한다. */
export const quoteCalendarDateSchema = z.iso.date();
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
/**
 * 기준 시각의 한국(UTC+9) 달력 날짜(YYYY-MM-DD). api 의 견적 정책과 worker 의 만료 작업이 같은 "한국 오늘"을 쓰도록
 * 여기(공용 계약)에 둔다 — 서버 타임존이나 실행 시각의 전역 상태에 의존하지 않는다. 유효하지 않은 시각은 던진다.
 */
export const koreanCalendarDate = (now: Date): string => {
  if (!Number.isFinite(now.getTime())) throw new RangeError('유효하지 않은 기준 시각입니다.');
  return quoteCalendarDateSchema.parse(
    new Date(now.getTime() + KST_OFFSET_MS).toISOString().slice(0, 10),
  );
};
/**
 * 기준 시각의 한국(UTC+9) 날짜·시각 표기 `YYYY-MM-DD HH:mm`(분 아래는 버린다). `koreanCalendarDate` 와 같은 오프셋을 써서
 * 날짜 부분이 항상 같다 — 주문 메일의 요청 시각·승인 일시처럼 사람에게 보이는 시각을 서버 타임존과 무관하게 만든다.
 * 유효하지 않은 시각은 던진다.
 */
export const koreanDateTime = (value: Date): string => {
  if (!Number.isFinite(value.getTime())) throw new RangeError('유효하지 않은 기준 시각입니다.');
  const shifted = new Date(value.getTime() + KST_OFFSET_MS).toISOString();
  return `${shifted.slice(0, 10)} ${shifted.slice(11, 16)}`;
};
const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_QUOTE_VALID_DAYS = 7;
/**
 * 발행 기본 만료일 = 한국 오늘 + 7일(YYYY-MM-DD, 견적 설계 §6-3). 달력 날짜끼리의 UTC 산술이라 시간대에 흔들리지 않는다.
 * `kstToday` 는 호출자가 `koreanCalendarDate(now)` 로 만든다 — api 재발행 초안과 admin 편집기가 같은 함수를 쓴다.
 */
export const defaultQuoteValidUntil = (kstToday: string): string =>
  quoteCalendarDateSchema.parse(
    new Date(Date.parse(`${kstToday}T00:00:00Z`) + DEFAULT_QUOTE_VALID_DAYS * DAY_MS)
      .toISOString()
      .slice(0, 10),
  );
export const quoteRequiredTextSchema = z.string().trim().min(1);
export const quoteOptionalTextSchema = (max?: number): z.ZodType<string | null> => {
  const text = max === undefined ? z.string().trim() : z.string().trim().max(max);
  return text.nullable().transform((value) => (value === '' ? null : value));
};

/**
 * 배송지는 견적 요청·주문하기·주문 배송지 변경이 함께 쓰는 단일 계약(`./shipping-address`)이다. index 의 `export *` 가
 * 같은 이름을 두 번 내보내지 않도록 이 파일에서만 재노출한다.
 */
export {
  SHIPPING_ADDRESS_MAX_LENGTHS,
  type ShippingAddress,
  shippingAddressSchema,
} from './shipping-address';

export const DESTINATION_QUANTITY_TOTAL_TOO_LARGE_MESSAGE = '배송지 수량 합계가 너무 큽니다.';
/** 견적 수량 = 배송지 수량 합계(spec D5). 서버가 저장하고 web 이 총 수량으로 보여 준다. 검증은 하지 않는다. */
export const totalDestinationQuantity = (
  destinations: readonly { readonly quantity: number }[],
): number => destinations.reduce((sum, destination) => sum + destination.quantity, 0);
/** 합계가 int4 를 넘으면 `destinations` 경로에 한 번 알린다 — 회원·비회원 입력이 함께 쓴다. */
export const refineDestinationTotal = (
  destinations: readonly { readonly quantity: number }[] | undefined,
  ctx: z.RefinementCtx,
): void => {
  if (destinations !== undefined && totalDestinationQuantity(destinations) > QUOTE_INT_MAX)
    ctx.addIssue({
      code: 'custom',
      path: ['destinations'],
      message: DESTINATION_QUANTITY_TOTAL_TOO_LARGE_MESSAGE,
    });
};

/** 견적 요청(spec §5-1) — 견적 수량은 받지 않고 서버가 배송지 수량 합계로 계산한다. */
export const quoteRequestInputSchema = z
  .strictObject({
    designId: uuidSchema,
    printMethodId: uuidSchema,
    desiredDate: quoteCalendarDateSchema.nullable(),
    destinations: destinationListSchema(memberDestinationInputSchema),
    memo: quoteOptionalTextSchema(1000),
    companyName: quoteOptionalTextSchema(100),
    contactName: quoteRequiredTextSchema.max(50),
  })
  .superRefine((input, ctx) => {
    refineDestinationTotal(input.destinations, ctx);
    refineDestinationSizes(input.destinations, ctx);
  });
export type QuoteRequestInput = z.infer<typeof quoteRequestInputSchema>;
export const quoteRequestIdInputSchema = z.strictObject({ quoteRequestId: uuidSchema });
export const quoteVersionIdInputSchema = quoteRequestIdInputSchema.extend({
  versionId: uuidSchema,
});
export const quoteRequestFormInputSchema = z.strictObject({ designId: uuidSchema });
export const quoteAssetDownloadInputSchema = quoteRequestIdInputSchema.extend({
  assetId: uuidSchema,
});

export const rejectQuoteInputSchema = quoteRequestIdInputSchema.extend({
  reason: quoteRequiredTextSchema.max(500),
});
export const requestQuoteRevisionInputSchema = quoteVersionIdInputSchema.extend({
  reason: quoteRequiredTextSchema.max(1000),
});
export const declineQuoteInputSchema = quoteRequestIdInputSchema.extend({
  reason: quoteOptionalTextSchema(500),
});
export const addQuoteNoteInputSchema = quoteRequestIdInputSchema.extend({
  body: quoteRequiredTextSchema.max(2000),
});

/**
 * 라우터 출력 계약(spec §11-2). strictObject 라 유스케이스 결과에 내부 필드가 섞이면 출력 검증에서 거부된다.
 * 결과가 없는 변경(requestRevision·decline·cancelRequest·saveDraft·issue·reject)은 undefined 만 내보낸다.
 */
export const quoteRequestIdResultSchema = z.strictObject({ quoteRequestId: uuidSchema });
export const quoteVersionIdResultSchema = z.strictObject({ versionId: uuidSchema });
export const quoteNoteIdResultSchema = z.strictObject({ noteId: uuidSchema });
/** 본인인증 전 회원·탈퇴로 파기된 경우 null. */
export const quotePhoneRevealSchema = z.strictObject({ phone: z.string().nullable() });
/** 비공개 버킷의 서명 URL — 만료가 있으므로 저장하지 않고 조회 때마다 받는다. */
export const quoteAssetDownloadUrlSchema = z.strictObject({ url: z.url() });
export const quoteEmptyResultSchema = z.void();

export const quoteAdminListInputSchema = paginationSchema.extend({
  statuses: z
    .array(quoteRequestStatusSchema)
    .min(1)
    .max(QUOTE_REQUEST_STATUSES.length)
    .default([...DEFAULT_ADMIN_QUOTE_STATUSES]),
  /** 견적 번호·회사명·담당자명 검색. */
  search: z.string().trim().optional(),
});
export type QuoteAdminListInput = z.infer<typeof quoteAdminListInputSchema>;
export const quotePriceTierSchema = z.strictObject({
  minQty: quoteQuantitySchema,
  unitPrice: quoteIntegerSchema.min(1),
});
export const quoteRequestFormSchema = z.strictObject({
  designId: uuidSchema,
  productName: z.string(),
  colorName: z.string(),
  minOrderQty: quoteQuantitySchema,
  priceTiers: z.array(quotePriceTierSchema),
  printMethods: z.array(z.strictObject({ id: uuidSchema, name: z.string() })),
  /** 사이즈 옵션 — 없으면 빈 배열(사이즈 없는 상품). */
  sizeOptions: z.array(productSizeOptionSchema),
  defaultContactName: z.string(),
  /**
   * 원본 디자인의 지금 썸네일(견적 디자인 미리보기 설계 D9) — 아직 견적이 없어 견적 썸네일이 아니다. 디자인 목록과 같은 규칙
   * (저장 시각이 같을 때만, 편집영역·렌더러 버전만 다르면 isStale)이고 없으면 null(화면은 '미리보기 준비 중').
   */
  thumbnail: designThumbnailViewSchema.nullable(),
  /** 요청 순간의 배송비 설정(spec §6-1 임시 견적) — 제출 때는 서버가 다시 읽는다. */
  shippingFeeSettings: shippingFeeSettingsSchema,
});
export type QuoteRequestForm = z.infer<typeof quoteRequestFormSchema>;

export const quoteRowSchema = z.strictObject({
  id: uuidSchema,
  quoteNumber: z.string(),
  createdAt: z.iso.datetime(),
  productName: z.string(),
  quantity: quoteQuantitySchema,
  status: quoteRequestStatusSchema,
  latestIssuedTotalAmount: quoteIntegerSchema.nullable(),
  /**
   * 이 견적의 살아 있는 주문(`CANCELED` 아님 — 견적당 최대 하나) id 와 주문 고객 화면 이름(주문 설계 §4-3, 예: 결제 대기·제작 중·
   * 환불 완료). 주문 모듈이 계산해 견적 조회에 붙인다(R18, `QuoteOrderSummaryPort`). 살아 있는 주문이 없으면 둘 다 null 이다.
   */
  orderId: uuidSchema.nullable(),
  orderDisplayStatus: z.string().nullable(),
  /**
   * 견적 사본 썸네일 표시 주소(설계 D9·D10) — `quote_requests.thumbnail_key` 를 조회 때마다 서명한다(만료가 있어 저장하지 않는다).
   * worker 가 아직 그리지 않았거나 그릴 수 없는 견적이면 null. 상세 계약은 이 칸을 빼고 `design.thumbnailUrl` 을 쓴다.
   */
  thumbnailUrl: z.string().nullable(),
  /** 상품 대표 이미지 표시 주소 — 일반 견적에만 싣는다(디자인 썸네일 대신 목록·상세가 보인다, 일반 견적 설계 §6). 디자인 견적이거나 이미지가 없으면 null. */
  productImageUrl: z.string().nullable(),
});
export type QuoteRow = z.infer<typeof quoteRowSchema>;
export const quoteWarningsSchema = z.strictObject({
  productHidden: z.boolean(),
  productDeleted: z.boolean(),
  colorUnavailable: z.boolean(),
});
export type QuoteWarnings = z.infer<typeof quoteWarningsSchema>;
/**
 * 관리자 목록의 비회원 표시(비회원 견적 설계 G14) — 연락 방법과 회원 계정 연결 여부만 싣는다. 이메일·휴대폰 값은 목록에 싣지 않는다.
 * 연결된 뒤에도 '비회원 접수' 출처로 남는다(G2).
 */
export const adminQuoteGuestRowSchema = z.strictObject({
  contactMethod: guestContactMethodSchema,
  claimed: z.boolean(),
});
export type AdminQuoteGuestRow = z.infer<typeof adminQuoteGuestRowSchema>;
export const adminQuoteRowSchema = quoteRowSchema.extend({
  companyName: z.string().nullable(),
  contactName: z.string(),
  desiredDate: quoteCalendarDateSchema.nullable(),
  warnings: quoteWarningsSchema,
  /** 회원이 요청한 견적은 null. */
  guest: adminQuoteGuestRowSchema.nullable(),
});
export type AdminQuoteRow = z.infer<typeof adminQuoteRowSchema>;
export const quotePageSchema = paginatedSchema(quoteRowSchema);
export const adminQuotePageSchema = paginatedSchema(adminQuoteRowSchema);
export type QuotePage<TRow extends QuoteRow = QuoteRow> = Omit<
  z.infer<typeof quotePageSchema>,
  'items'
> & { items: TRow[] };
